import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { change, Field, FieldArray, reduxForm } from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Typography } from '@material-ui/core';

import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import HelpIcon from '@material-ui/icons/Help';

import {
  CircularLoader,
  DropDown,
  MultiDatePickerCalendar,
  PopoverWrapper,
  RadioButtonsGroup,
  SingleDatePicker
} from 'components';

import {
  getReceivedActivityBookingsDates,
  getReceivedActivityData
} from 'utils/accommodations-and-activities/prepareReceivedActivityData';
import { getParticipants } from 'utils/book-activity/prepareParticipants';
import {
  getAvailableRangeDates,
  getDefaultStartTime,
  getDisabledDates,
  getListOfStartTimeAndDuration,
  getListOfStartTimeAndDurationByCutoffPeriod
} from 'utils/book-activity/availableDates';
import {
  getMaxParticipants,
  getMaxParticipantsActivitySeveralDays
} from 'utils/book-activity/maxParticipants';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import NumberOfParticipants from 'pages/book-activity/components/number-of-participants/NumberOfParticipants';
import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';

import {
  ACTIVITIES_AVAILABLE_PARTICIPANTS,
  ACTIVITY
} from 'graphql/queries/activities';

import validate from './validate';

import { useStyles } from '../styles';

const initialValues = {
  participants: [],
  addOns: []
};

const SCHEDULE_DATE_NO_TIME_NAME = 'scheduleDateForm';
const SCHEDULE_DATE_AND_TIME_NAME = 'scheduleDateTimeForm';
const SCHEDULE_PASS_NAME = 'schedulePassForm';
const FUTURE_PRICES = 'activityFuturePricesForm';

const BookActivityDialog = ({
  open,
  ownerId,
  onCancel,
  onConfirm,
  onUpdate,
  relatedActivitiesForm,
  currency,
  bookRelatedActivityForm,
  changeFormFieldValue,
  handleSubmit,
  initialValues
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const [isInitial, setIsInitial] = React.useState(!!initialValues.title);
  const [activityId, setActivityId] = React.useState(null);
  const [activity, setActivity] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );
  const [
    listStartTimeAndDuration,
    setListStartTimeAndDuration
  ] = React.useState([]);
  const [
    availableParticipantsData,
    setAvailableParticipantsData
  ] = React.useState(null);

  const initialStartingDate =
    initialValues && initialValues.startingDate
      ? initialValues.startingDate
      : initialValues && initialValues.multiDates
      ? initialValues.multiDates.startDate
      : moment();

  const { loading, data } = useQuery(ACTIVITY, {
    skip: !activityId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId,
      businessOwnerId: ownerId
    }
  });

  const {
    loading: isAvailableParticipantsLoading,
    data: receivedAvailableParticipantsData,
    refetch: refetchParticipantsData
  } = useQuery(ACTIVITIES_AVAILABLE_PARTICIPANTS, {
    skip: !activityId,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      activityId: activityId,
      businessOwnerId: ownerId,
      startPeriod: moment(initialStartingDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(initialStartingDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (!receivedAvailableParticipantsData) return;

    const availableParticipantsData = getReceivedActivityBookingsDates(
      receivedAvailableParticipantsData
    );

    setAvailableParticipantsData(availableParticipantsData);
  }, [receivedAvailableParticipantsData]);

  React.useEffect(() => {
    if (data && data.activities.length) {
      const receivedActivity = data.activities[0];
      const activityData = getReceivedActivityData(receivedActivity);

    if (receivedActivity?.futurePrices?.length > 0) {
      const hasValidFuturePrices = receivedActivity.futurePrices.some(
        fp => fp.ratePlanPrices && fp.ratePlanPrices.length > 0
      );
      
      if (hasValidFuturePrices) {
        activityData['futurePriceForm'] = receivedActivity['futurePriceForm'];
        
        activityData['pricesType'] = receivedActivity['pricesType'];  
      }
    } else {

      activityData['pricesType'] = receivedActivity['pricesType'];
    }
      

      activityData['pricesType'] = data.activities[0]['pricesType'];
      activityData['scheduleType'] = data.activities[0]['scheduleType'];

      if (!isInitial) {
        changeFormFieldValue('title', activityData.generalForm.title);
        changeFormFieldValue('startingDate' && 'multiDates', null);
        changeFormFieldValue('maxParticipants', null);
        changeFormFieldValue('participants', []);
        changeFormFieldValue('futurePriceForm', activityData['futurePriceForm']);
        changeFormFieldValue('pricesType', activityData['pricesType']);
        setListStartTimeAndDuration([]);
      } else {
        handleListStartTimeAndDurationField(activityData);
        setParticipants(activityData);
      }

      setMaxParticipants(activityData['generalForm'].maxParticipants);

      setAddOns(activityData);

      if (activityData.scheduleType === SCHEDULE_PASS_NAME) {
        if (!isInitial) {
          setParticipants(activityData);
        }
      }

      setIsInitial(false);
      setActivity(activityData);
    }
  }, [data]);

  React.useEffect(() => {
    const selectedActivityId = bookRelatedActivityForm.values.activity;

    if (selectedActivityId) {
      setActivityId(selectedActivityId);
    }
  }, [bookRelatedActivityForm.values.activity]);

  React.useEffect(() => {
    const startingDate = bookRelatedActivityForm.values.startingDate;

    if (startingDate) {
      handleListStartTimeAndDurationField(activity);
      setParticipants(activity);
    }
  }, [bookRelatedActivityForm.values.startingDate]);

  React.useEffect(() => {
    const multiDates = bookRelatedActivityForm.values.multiDates;
    if (multiDates) {
      handleMultiDateAvailableSeats(activity);
      setParticipants(activity);
    }
  }, [
    activity,
    availableParticipantsData,
    bookRelatedActivityForm.values.multiDates
  ]);

  React.useEffect(() => {
    const startTime = bookRelatedActivityForm.values.startTime;

    if (startTime) {
      setMaxParticipants(startTime.seatsLeft);
    }
  }, [bookRelatedActivityForm.values.startTime]);

  const setMaxParticipants = seatsLeft => {
    const maxParticipants = getMaxParticipants(
      seatsLeft,
      initialValues,
      bookRelatedActivityForm,
      availableParticipantsData
    );

    changeFormFieldValue('maxParticipants', maxParticipants);
  };

  const changeMonthHandler = date => {
    refetchParticipantsData({
      activityId: parseInt(activityId),
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const setParticipants = activity => {
    const previousParticipants =
      bookRelatedActivityForm.values.participants || [];
    const participants = getParticipants(
      activity,
      bookRelatedActivityForm,
      setIsUpdatedParticipants,
      isUpdatedParticipants,
      currency,
      previousParticipants
    );

    changeFormFieldValue('participants', participants);
  };

  const setAddOns = activity => {
    const previousAddOns = bookRelatedActivityForm.values.addOns || [];

    const addOnsList = activity.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const handleMultiDateAvailableSeats = activity => {
    if (!activity || !bookRelatedActivityForm.values.multiDates) return;

    const startDate = bookRelatedActivityForm.values.multiDates.startDate;
    const endDate = bookRelatedActivityForm.values.multiDates.endDate;

    if (!startDate || !endDate) return;

    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];

    const availableParticipants = getMaxParticipantsActivitySeveralDays(
      availableParticipantsPerDate,
      startDate,
      endDate,
      initialValues
    );

    if (availableParticipants !== null) {
      setMaxParticipants(availableParticipants);
    } else {
      setMaxParticipants(activity.generalForm.maxParticipants);
    }
  };

  const handleListStartTimeAndDurationField = activity => {
    if (!activity) return;

    const startingDate = bookRelatedActivityForm.values.startingDate;
    const availableParticipantsPerDate = availableParticipantsData
      ? availableParticipantsData.activities[0].availableParticipantsPerDate
      : [];
    let listOfStartTimeAndDuration = getListOfStartTimeAndDuration(
      availableParticipantsPerDate,
      activity,
      bookRelatedActivityForm
    );
    listOfStartTimeAndDuration = getListOfStartTimeAndDurationByCutoffPeriod(
      activity,
      listOfStartTimeAndDuration,
      startingDate
    );
    const selectedStartTime = getDefaultStartTime(
      listOfStartTimeAndDuration,
      bookRelatedActivityForm
    );
    changeFormFieldValue('startTime', selectedStartTime);

    if (!selectedStartTime) {
      setMaxParticipants(activity.generalForm.maxParticipants);
      availableParticipantsPerDate.map(item => {
        const formattedStartDate = startingDate
          ? moment(startingDate).format('YYYY-MM-DD')
          : '';
        if (!item.startTime && item.date === formattedStartDate) {
          setMaxParticipants(item.availableParticipants);
        }
      });
    } else {
      setMaxParticipants(selectedStartTime.seatsLeft);
    }

    setListStartTimeAndDuration(listOfStartTimeAndDuration);
  };

  const getStartTimeAndDuration = () => {
    return listStartTimeAndDuration
      .map(item => {
        return {
          ...item,
          title: `${item.startTime} • ${item.duration} ${t(item.timeType)}`
        };
      })
      .filter(item => item.seatsLeft);
  };

  const confirmWrapper = item => {
    const generalFormNorwegian = activity['generalForm-Norwegian'];
    const norwegianFields = {};

    if (generalFormNorwegian) {
      norwegianFields['title'] = generalFormNorwegian.title;
    }
    onConfirm({
      ...item,
      Norwegian: norwegianFields
    });
  };

  const generalForm = activity
    ? getFormByCurrentLanguage(
        selectedLanguage,
        activity.generalForm,
        activity['generalForm-Norwegian']
      )
    : {};

  return (
    <Dialog open={open} scroll="paper" classes={{ paper: classes.paper }}>
      <CircularLoader
        isLoading={loading || isAvailableParticipantsLoading}
        loaderClassName={classes.loaderClassName}
      />

      <IconButton className={classes.closeBtn} onClick={onCancel}>
        <HighlightOffIcon className={classes.closeIcon} />
      </IconButton>

      <DialogTitle
        id="alert-dialog-title"
        classes={{ root: classes.title }}
        disableTypography={true}
      >
        {activity ? generalForm.title : t('Book Activity')}
        <PopoverWrapper
          text={`Note you can book only an activity that is related to its accommodation`}
        >
          <HelpIcon className={classes.helpIcon} />
        </PopoverWrapper>
      </DialogTitle>

      <form
        onSubmit={handleSubmit(item =>
          !!initialValues.title ? onUpdate(item) : confirmWrapper(item)
        )}
      >
        <DialogContent classes={{ root: classes.contentRoot }}>
          {activity && (
            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {activity.generalForm.startingPoint}
                </Typography>
              </div>
              {bookRelatedActivityForm.values.maxParticipants >= 0 && (
                <div className={classes.infoItem}>
                  <PeopleOutlineIcon classes={{ root: classes.infoIcon }} />
                  <Typography variant="body1">
                    {bookRelatedActivityForm.values.maxParticipants}{' '}
                    {t('Participants')}
                  </Typography>
                </div>
              )}
            </div>
          )}

          <Field
            name="activity"
            className={classes.inputIndent}
            list={relatedActivitiesForm.relatedActivities}
            field="title"
            label="Activity *"
            component={DropDown}
          />

          {activity && (
            <>
              {activity.scheduleType === SCHEDULE_DATE_NO_TIME_NAME && (
                <Field
                  name="multiDates"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    initialValues && initialValues.multiDates,
                    activity
                  )}
                  changeMonthsCallback={changeMonthHandler}
                  isInput={true}
                  numberOfMonths={2}
                  daySize={39}
                  component={MultiDatePickerCalendar}
                  cutoffData={activity[activity.scheduleType]}
                  isStartDateRenderMonth={
                    !!(initialValues && initialValues.multiDates)
                  }
                />
              )}

              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME && (
                <Field
                  name="startingDate"
                  label="Starting date *"
                  availableRange={getAvailableRangeDates(activity)}
                  disabledDates={getDisabledDates(
                    availableParticipantsData,
                    initialValues && initialValues.startingDate,
                    activity
                  )}
                  changeMonthsCallback={changeMonthHandler}
                  isInput={true}
                  numberOfMonths={2}
                  daySize={39}
                  component={SingleDatePicker}
                  cutoffData={activity[activity.scheduleType]}
                  isStartDateRenderMonth={
                    !!(initialValues && initialValues.startingDate)
                  }
                />
              )}

              {!!bookRelatedActivityForm.values.participants.length && (
                <>
                  <Typography variant="body1" className={classes.sectionTitle}>
                    {t('Guests')} *
                  </Typography>
                  <FieldArray
                    name="participants"
                    className={classes.participants}
                    errorClassName={classes.participantsErrorMsg}
                    isUpdatedParticipants={isUpdatedParticipants}
                    component={NumberOfParticipants}
                    currency={currency}
                  />
                </>
              )}

              {activity.scheduleType === SCHEDULE_DATE_AND_TIME_NAME &&
                !!listStartTimeAndDuration.length && (
                  <>
                    <Typography
                      variant="body1"
                      className={classes.smallSectionTitle}
                    >
                      {t('Start time & Duration')}
                    </Typography>
                    <Field
                      name="startTime"
                      list={getStartTimeAndDuration()}
                      component={RadioButtonsGroup}
                      isId={true}
                      checkboxTitleField="title"
                      radioGroupRootClass={classes.radioGroupRoot}
                    />
                  </>
                )}

              {!!bookRelatedActivityForm.values.addOns.length && (
                <>
                  <Typography variant="body1" className={classes.sectionTitle}>
                    {t('Add-ons')}
                  </Typography>
                  <FieldArray
                    name="addOns"
                    currency={currency}
                    addOnDescClass={classes.addOnTitle}
                    component={AddOnsList}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions classes={{ root: classes.actionsRoot }}>
          <Button
            onClick={onCancel}
            variant="outlined"
            classes={{ root: classes.cancelBtnRoot }}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            classes={{ root: classes.actionBtnRoot }}
            autoFocus
          >
            {t('Book')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {
    bookRelatedActivityForm: state.form.bookRelatedActivityForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookRelatedActivityForm', field, value));
  }
});

export default reduxForm({
  form: 'bookRelatedActivityForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookActivityDialog));
